/**
 * Tenant Validation Schemas
 *
 * Validation schemas for tenant registration, updates, and verification.
 * Tenants are the top-level multi-tenant organizations in Ketrics.
 *
 * CRITICAL: This file is a 1:1 copy of tenant-api/src/validators/tenant.ts
 */

import { z } from 'zod';
import {
  emailSchema,
  tenantCodeSchema,
  nonEmptyStringSchema,
  countrySchema,
  citySchema,
  addressSchema,
  phoneNumberSchema,
  verificationTokenSchema,
  nameSchema,
  passwordSchema,
} from './common';

// ============================================================================
// TENANT SETTINGS VALIDATION
// ============================================================================

/**
 * Tenant settings validation schema
 * Settings are flexible key-value pairs with optional predefined fields
 */
export const tenantSettingsSchema = z
  .object({
    maxUsers: z
      .number({
        invalid_type_error: 'maxUsers must be a number',
      })
      .int('maxUsers must be an integer')
      .positive('maxUsers must be positive')
      .optional(),
    features: z
      .array(
        z.string({
          invalid_type_error: 'Feature must be a string',
        }),
        {
          invalid_type_error: 'features must be an array of strings',
        }
      )
      .optional(),
    defaultRoleSessionDurationHours: z
      .number({
        invalid_type_error: 'defaultRoleSessionDurationHours must be a number',
      })
      .int('defaultRoleSessionDurationHours must be an integer')
      .min(1, 'defaultRoleSessionDurationHours must be at least 1 hour')
      .max(8, 'defaultRoleSessionDurationHours must not exceed 8 hours')
      .optional(),
  })
  .passthrough() // Allow additional properties
  .optional();

export type TenantSettingsValidation = z.infer<typeof tenantSettingsSchema>;

// ============================================================================
// TENANT REGISTRATION VALIDATION
// ============================================================================

/**
 * Tenant registration schema
 * Used when a new organization signs up for Ketrics
 *
 * Required fields:
 * - tenantCode: Unique identifier (lowercase alphanumeric with hyphens/underscores)
 * - tenantName: Organization name
 * - adminEmail: Email of the tenant administrator
 *
 * Optional fields:
 * - country, city, address, phoneNumber: Organization details
 * - settings: Custom tenant configuration
 */
export const tenantRegistrationSchema = z
  .object({
    tenantCode: tenantCodeSchema,
    tenantName: nonEmptyStringSchema
      .min(1, 'Tenant name must be at least 1 character long')
      .max(200, 'Tenant name must not exceed 200 characters'),
    adminEmail: emailSchema,
    country: countrySchema,
    city: citySchema,
    address: addressSchema,
    phoneNumber: phoneNumberSchema,
    settings: tenantSettingsSchema,
  })
  .strict(); // No additional properties allowed

export type TenantRegistrationValidation = z.infer<typeof tenantRegistrationSchema>;

// ============================================================================
// TENANT UPDATE VALIDATION
// ============================================================================

/**
 * Tenant update schema
 * Used for updating tenant information
 *
 * All fields are optional (at least one must be provided)
 * Cannot update tenantCode (immutable after creation)
 */
export const tenantUpdateSchema = z
  .object({
    tenantName: nonEmptyStringSchema
      .min(1, 'Tenant name must be at least 1 character long')
      .max(200, 'Tenant name must not exceed 200 characters')
      .optional(),
    country: countrySchema,
    city: citySchema,
    address: addressSchema,
    phoneNumber: phoneNumberSchema,
    settings: tenantSettingsSchema,
  })
  .strict()
  .refine(
    (data) => Object.keys(data).length > 0,
    'At least one field must be provided for update'
  );

export type TenantUpdateValidation = z.infer<typeof tenantUpdateSchema>;

// ============================================================================
// TENANT VERIFICATION VALIDATION
// ============================================================================

/**
 * Tenant verification schema
 * Used when verifying tenant email after registration
 */
export const tenantVerificationSchema = z
  .object({
    verificationToken: verificationTokenSchema,
  })
  .strict();

export type TenantVerificationValidation = z.infer<typeof tenantVerificationSchema>;

/**
 * Tenant verification with admin user creation schema
 * Used when verifying tenant email and creating admin user in one step
 *
 * Required fields:
 * - verificationToken: Token from verification email
 * - firstName: Admin user's first name
 * - lastName: Admin user's last name
 * - password: Admin user's password (min 8 chars, strong password requirements)
 */
export const tenantVerificationWithUserSchema = z
  .object({
    verificationToken: verificationTokenSchema,
    firstName: nameSchema,
    lastName: nameSchema,
    password: passwordSchema,
  })
  .strict();

export type TenantVerificationWithUserValidation = z.infer<typeof tenantVerificationWithUserSchema>;

/**
 * Verification form data schema (for form fields only)
 * Token is passed separately from URL params
 */
export const verificationFormDataSchema = z
  .object({
    firstName: nameSchema,
    lastName: nameSchema,
    password: passwordSchema,
  })
  .strict();

export type VerificationFormData = z.infer<typeof verificationFormDataSchema>;

// ============================================================================
// TENANT CODE AVAILABILITY CHECK
// ============================================================================

/**
 * Tenant code availability check schema
 * Used to validate tenant code format before checking availability
 */
export const tenantCodeCheckSchema = z
  .object({
    tenantCode: tenantCodeSchema,
  })
  .strict();

export type TenantCodeCheckValidation = z.infer<typeof tenantCodeCheckSchema>;
